class FpProductCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.carouselReady) return;

    this.viewport = this.querySelector('[data-carousel-viewport]');
    this.track = this.querySelector('[data-carousel-track]');
    this.cards = Array.from(this.querySelectorAll('[data-carousel-card]'));
    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.currentLabel = this.querySelector('[data-carousel-current]');
    this.totalLabel = this.querySelector('[data-carousel-total]');

    if (!this.viewport || !this.track || this.cards.length === 0) return;

    this.dataset.carouselReady = 'pending';
    const observerTarget = this.closest('.fp-entity-sections__row') || this;

    if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
        this.initializeCarousel();
      }, { rootMargin: '700px 0px' });
      this.visibilityObserver.observe(observerTarget);
      return;
    }

    this.initializeCarousel();
  }

  initializeCarousel() {
    if (this.dataset.carouselReady === 'true') return;
    this.dataset.carouselReady = 'true';
    this.currentPage = 0;
    this.scrollFrame = null;

    this.previousButton?.addEventListener('click', () => this.goToPage(this.currentPage - 1));
    this.nextButton?.addEventListener('click', () => this.goToPage(this.currentPage + 1));
    this.viewport.addEventListener('scroll', () => {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.update());
    }, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.viewport);
    this.initializeHoverImages();
    this.update();
  }

  disconnectedCallback() {
    this.visibilityObserver?.disconnect();
    this.resizeObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  get pageSize() {
    return window.matchMedia('(max-width: 899px)').matches ? 2 : 4;
  }

  initializeHoverImages() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.querySelectorAll('[data-hover-image-src]').forEach((media) => {
      media.addEventListener('pointerenter', () => this.loadHoverImage(media), { once: true });
    });
  }

  loadHoverImage(media) {
    if (media.querySelector('.fp-product-row__image--hover')) return;

    const image = new Image();
    image.className = 'fp-product-row__image fp-product-row__image--hover';
    image.alt = '';
    image.decoding = 'async';
    image.fetchPriority = 'low';
    image.sizes = '(min-width: 900px) 23vw, 46vw';
    image.width = Number(media.dataset.hoverImageWidth) || 900;
    image.height = Number(media.dataset.hoverImageHeight) || 900;
    image.addEventListener('load', () => media.classList.add('is-hover-image-ready'), { once: true });
    image.srcset = media.dataset.hoverImageSrcset || '';
    image.src = media.dataset.hoverImageSrc;
    media.appendChild(image);
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.cards.length / this.pageSize));
  }

  goToPage(page) {
    const nextPage = Math.max(0, Math.min(page, this.totalPages - 1));
    const targetCard = this.cards[nextPage * this.pageSize];
    if (!targetCard) return;

    this.viewport.scrollTo({
      left: targetCard.offsetLeft - this.track.offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  update() {
    const firstCard = this.cards[0];
    if (!firstCard) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const cardStep = firstCard.getBoundingClientRect().width + gap;
    const visibleIndex = cardStep > 0 ? Math.round(this.viewport.scrollLeft / cardStep) : 0;
    const scrollTolerance = 2;
    const maximumScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    const isAtStart = this.viewport.scrollLeft <= scrollTolerance;
    const isAtEnd = this.viewport.scrollLeft >= maximumScroll - scrollTolerance;

    if (isAtStart) {
      this.currentPage = 0;
    } else if (isAtEnd) {
      this.currentPage = this.totalPages - 1;
    } else {
      this.currentPage = Math.min(this.totalPages - 1, Math.floor(visibleIndex / this.pageSize));
    }

    if (this.currentLabel) this.currentLabel.textContent = String(this.currentPage + 1);
    if (this.totalLabel) this.totalLabel.textContent = String(this.totalPages);

    if (this.previousButton) {
      this.previousButton.disabled = isAtStart;
      this.previousButton.hidden = maximumScroll <= scrollTolerance;
    }

    if (this.nextButton) {
      this.nextButton.disabled = isAtEnd;
      this.nextButton.hidden = maximumScroll <= scrollTolerance;
    }
  }
}

if (!customElements.get('fp-product-carousel')) {
  customElements.define('fp-product-carousel', FpProductCarousel);
}
